<script lang="ts">
export default {
  name: 'PolicyDocumentUploadListModal',
};
</script>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import AppButton from '@/components/v2/AppButton.vue';
import clientReadinessService, { ClientReadinessPolicy } from '@/services/v2/ClientReadinessService';
import UploadPolicyDocumentModal from '@/components/v2/UploadPolicyDocumentModal.vue';
import { documentService } from '@/services/v2/DocumentService';
import { Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { UploadDocumentRequest, DocumentType, UploadDocumentResponse } from '@/types/v2/Document';

const props = withDefaults(defineProps<{
  accountId: number;
  ShowNonCAC: boolean;
  ShowCurrentOnly: boolean;
  docType?: string;
  title?: string;
}>(), {
  docType: "Policy",
  title: "Upload Policy Documents",
});

const emit = defineEmits<{
  (e: 'document-uploaded'): void;
  (e: 'close'): void;
}>();

// refs
// -----
const policies = ref<ClientReadinessPolicy[]>();
const selectedPolicy = ref<ClientReadinessPolicy | null>(null);
const showUploadDocumentModal = ref(false);
const policyDocumentTypes = ref<DocumentType[]>([
]);

// methods
// --------
async function attachFilesToPolicy(uploadRequests: Partial<UploadDocumentRequest>[]): Promise<UploadDocumentResponse> {
  if (!selectedPolicy.value) {
    return { success: false, messages: ['No policy selected'] };
  }

  const policyID = selectedPolicy.value.policyId;

  try {
    const uploadDocumentPromises = uploadRequests.map((request) =>
      documentService.uploadDocumentForPolicyOrAccount({
        ...request,
        accountId: props.accountId,
        policyId: policyID,
      } as UploadDocumentRequest),
    );

    await Promise.all(uploadDocumentPromises)
      .then(async () => {
        Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Document attached successfully!' });
        showUploadDocumentModal.value = false;
        selectedPolicy.value = null;
        await refresh();
        emit('document-uploaded');
      })
      .catch((e) => {
        console.error(e);
        Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to attach document', color: 'error' });
        showUploadDocumentModal.value = false;
        selectedPolicy.value = null;
        return { success: false, messages: ['Failed to attach document'] };
      });
  } catch (e) {
    console.error(e);
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Failed to attach document', color: 'error' });
    showUploadDocumentModal.value = false;
    selectedPolicy.value = null;
    return { success: false, messages: ['Failed to attach document'] };
  }

  return { success: true, messages: [] };
}

function openDocumentUploadModal(policy: ClientReadinessPolicy) {
  selectedPolicy.value = policy;
  showUploadDocumentModal.value = true;
}

async function refresh() {
  //   fetch policies without documents
  policies.value = await clientReadinessService.getPoliciesWithoutDocuments(props.accountId, {
    showNonCAC: props.ShowNonCAC,
    showCurrentOnly: props.ShowCurrentOnly,
    documentType: props.docType,
  });
}

// lifecycles
// -----------
onMounted(async () => {
  policyDocumentTypes.value = (await documentService.getDocumentTypes()).filter((type) => type.name === (props.docType) && type.domainName !== 'Client Uploaded');
  await refresh();
});
</script>

<template>
  <AppModal :show="true" :title="title" size="xl">
    <template #content>
      <div class="max-h-[50vh] overflow-y-auto">
        <table class="apptable dense --tight-first">
          <thead>
            <tr>
              <td class="nosort">Policy</td>
              <td class="nosort">Description</td>
              <td class="nosort"></td>
            </tr>
          </thead>
          <tbody>
            <tr v-for="policy in policies" :key="policy.policyId" class="group">
              <td class="nosort font-semibold">{{ policy.mainPolicyNumber }}</td>
              <td class="nosort">{{ policy.description }}</td>
              <td class="nosort text-right">
                <AppButton small @click="openDocumentUploadModal(policy)">Upload</AppButton>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
    <template #actions>
      <AppButton primary @click="() => $emit('close')">Done</AppButton>
    </template>
  </AppModal>

  <UploadPolicyDocumentModal
    v-if="showUploadDocumentModal && selectedPolicy"
    title="Upload Document"
    :description="'Select document for Policy ' + selectedPolicy.mainPolicyNumber"
    :upload="(documents) => attachFilesToPolicy(documents)"
    :policy-document-types="policyDocumentTypes"
    accepted-content-types=""
    :is-user-allowed="true"
    @close="
      selectedPolicy = null;
      showUploadDocumentModal = false;
    "
  >
  </UploadPolicyDocumentModal>
</template>
